import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import { useClock, useConnectionStatus, SOC_SCREENS, PLACEHOLDER_ALERTS } from './useCommandData';
import useSocket, { playAlertSound } from 'hooks/useSocket';
import './commandement.css';

const GRAVITE_CLS = { critique: 'cmd-badge-critique', eleve: 'cmd-badge-eleve', moyen: 'cmd-badge-moyen', faible: 'cmd-badge-faible' };

export default function CommandementIncidents() {
  const navigate = useNavigate();
  const { timeStr, dateStr } = useClock();
  const [incidents, setIncidents] = useState([]);
  const [sites, setSites] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ site_id: '', type_incident: 'intrusion', description: '', gravite: 'moyen' });

  const fetchData = useCallback(async () => {
    const [inc, s] = await Promise.all([
      api.get('/incidents').then(r => r.data).catch(() => []),
      api.get('/sites').then(r => r.data).catch(() => []),
    ]);
    setIncidents(Array.isArray(inc) ? inc.filter(i => i.statut !== 'cloture') : []);
    setSites(s);
  }, []);

  const { connected, reconnected, lastTimeStr } = useConnectionStatus(fetchData, 10000);

  useSocket({
    'incident:nouveau': (data) => {
      playAlertSound(data.gravite || 'moyen');
      fetchData();
    },
    'alerte:nouvelle': () => fetchData(),
  });

  const activeIncidents = incidents.sort((a, b) => {
    const order = { critique: 0, eleve: 1, moyen: 2, faible: 3 };
    return (order[a.gravite] || 4) - (order[b.gravite] || 4);
  });

  const handleSubmit = async () => {
    if (!form.site_id || !form.description) return;
    try {
      await api.post('/incidents', form);
      setShowModal(false);
      setForm({ site_id: '', type_incident: 'intrusion', description: '', gravite: 'moyen' });
      fetchData();
    } catch { alert('Erreur creation incident'); }
  };

  return (
    <div className="cmd-root">
      {!connected && <div className="cmd-banner cmd-banner-error">⚠️ CONNEXION PERDUE — Derniere mise a jour : {lastTimeStr} — Reconnexion en cours...</div>}
      {reconnected && <div className="cmd-banner cmd-banner-ok">✅ CONNEXION RETABLIE</div>}

      <header className="cmd-header">
        <div className="cmd-header-left">
          <div className="cmd-logo">G</div>
          <span className="cmd-header-title">INCIDENTS & ALERTES EN COURS</span>
        </div>
        <div className="cmd-header-right">
          {activeIncidents.length > 0 && <span className="cmd-header-badge">{activeIncidents.length} actif{activeIncidents.length > 1 ? 's' : ''}</span>}
          <span className="cmd-date">{dateStr}</span>
          <span className="cmd-clock">{timeStr}</span>
          <button className="cmd-back-btn" onClick={() => navigate('/commandement')}>RETOUR</button>
        </div>
      </header>

      <div className="cmd-incidents-body">
        <div className="cmd-incidents-left">
          <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 11, letterSpacing: 2, color: '#888', marginBottom: 12 }}>INCIDENTS ACTIFS</div>
          {activeIncidents.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>Aucun incident actif — RAS</div>
          )}
          {activeIncidents.map(inc => (
            <div key={inc.id} className="cmd-incident-card">
              <div className="cmd-incident-header">
                <span className={`cmd-incident-badge ${GRAVITE_CLS[inc.gravite] || 'cmd-badge-moyen'}`}>
                  {(inc.gravite || 'moyen').toUpperCase()}
                </span>
                <span style={{ fontSize: 11, color: '#555' }}>{inc.statut === 'en_cours' ? 'EN TRAITEMENT' : 'EN COURS'}</span>
              </div>
              <div className="cmd-incident-title">{inc.type_incident || 'Incident'} — {inc.titre || inc.description?.slice(0, 50)}</div>
              <div className="cmd-incident-site">{inc.site_nom || 'Site inconnu'}</div>
              <div className="cmd-incident-meta">
                <span>Signale : {inc.created_at ? new Date(inc.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}</span>
                {inc.agent_nom && <span>Par : {inc.agent_nom}</span>}
              </div>
              <button className="cmd-incident-btn" onClick={() => navigate('/incidents')}>Prendre en charge</button>
            </div>
          ))}
        </div>

        <div className="cmd-incidents-right">
          <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 11, letterSpacing: 2, color: '#888', marginBottom: 12 }}>ALERTES SYSTEME</div>
          {PLACEHOLDER_ALERTS.map((al, i) => (
            <div key={al.id} className="cmd-sys-alert">
              <span className={`cmd-alert-dot ${al.level}`} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#E8E8E8', fontWeight: 600 }}>
                  {al.type}
                  {i === 0 && <span className="cmd-sys-alert-new">NOUVEAU</span>}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{al.desc}</div>
              </div>
              <span className="cmd-alert-time">{al.time}</span>
            </div>
          ))}
        </div>
      </div>

      <button className="cmd-fab" onClick={() => setShowModal(true)}>+ Signaler incident</button>

      {showModal && (
        <div className="cmd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cmd-modal" onClick={e => e.stopPropagation()}>
            <div className="cmd-modal-title">SIGNALER UN INCIDENT</div>
            <label>Site</label>
            <select value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
              <option value="">-- Sélectionner --</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <label>Type</label>
            <select value={form.type_incident} onChange={e => setForm(f => ({ ...f, type_incident: e.target.value }))}>
              <option value="intrusion">Intrusion</option>
              <option value="vol">Vol</option>
              <option value="alarme">Alarme</option>
              <option value="agression">Agression</option>
              <option value="incendie">Incendie</option>
              <option value="autre">Autre</option>
            </select>
            <label>Gravite</label>
            <select value={form.gravite} onChange={e => setForm(f => ({ ...f, gravite: e.target.value }))}>
              <option value="critique">Critique</option>
              <option value="eleve">Eleve</option>
              <option value="moyen">Moyen</option>
              <option value="faible">Faible</option>
            </select>
            <label>Description</label>
            <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Decrivez l'incident..." />
            <div className="cmd-modal-actions">
              <button className="cmd-modal-btn cmd-modal-btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="cmd-modal-btn cmd-modal-btn-primary" onClick={handleSubmit}>Envoyer</button>
            </div>
          </div>
        </div>
      )}

      <div className="cmd-status-bar">
        {SOC_SCREENS.map(s => (
          <div key={s.label} className="cmd-status-item">
            <span className="cmd-status-led" />
            <span className="cmd-status-label">{s.label} {s.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
